import { ChevronUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { IModifierGroup, IModifierItem } from '../../../home/types/Menu';

type IProps = {
  group: IModifierGroup;
  // Выбор внутри группы: itemId -> количество. Ненулевое значит «исключён».
  selected?: Record<string, number>;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onToggle: (item: IModifierItem, excluded: boolean) => void;
};

/**
 * Блок «Уберите ингредиенты»: тегами перечисляет состав блюда, из которого
 * можно что-то исключить.
 *
 * От остальных групп отличается только подачей — под капотом это обычные
 * модификаторы, поэтому исключённый ингредиент это позиция с количеством 1.
 * Он остаётся в списке и перечёркивается, повторное нажатие возвращает его.
 */
export const RemoveIngredients = ({
  group,
  selected,
  collapsed,
  onToggleCollapse,
  onToggle,
}: IProps) => (
  <div className="rounded-[16px] bg-white px-4 pt-[6px] pb-4">
    <button
      type="button"
      onClick={onToggleCollapse}
      className="flex w-full items-center justify-between gap-4 py-3 text-left"
    >
      <p className="font-inter-tight text-[16px] font-semibold leading-[20px] text-[#0B0D0F]">
        {group.name}
      </p>
      <ChevronUp
        className={cn(
          'h-5 w-5 shrink-0 text-text-tertiary transition-transform',
          collapsed && 'rotate-180'
        )}
      />
    </button>

    {!collapsed && (
      <div className="flex flex-wrap gap-3">
        {(group.items ?? []).map(item => {
          const excluded = (selected?.[item.itemId] ?? 0) > 0;

          return (
            <button
              key={item.itemId}
              type="button"
              aria-pressed={excluded}
              onClick={() => onToggle(item, excluded)}
              className={cn(
                // Рамка есть всегда, в обычном состоянии прозрачная: иначе
                // при исключении тег подрастал бы на 2px и ряд подпрыгивал.
                'relative inline-flex h-8 items-center justify-center rounded-full border-2 px-4 text-[16px] leading-5 text-[#0B0D0F] transition-colors',
                excluded
                  ? 'border-[#E1E4E8] bg-white'
                  : 'border-transparent bg-[#F2F4F7]'
              )}
            >
              {item.name}
              {/* Перечёркивание из макета. Рисуем диагональю по коробке тега,
                  а не полосой с фиксированным углом: у длинных тегов такая
                  полоса уезжала далеко за верх и низ. Здесь штрих всегда
                  выходит за бордер ровно на 2px, а угол подстраивается под
                  ширину. non-scaling-stroke держит толщину 2px, несмотря на
                  растяжение viewBox. */}
              {excluded && (
                <svg
                  aria-hidden
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  // Размеры заданы явно: svg — заменяемый элемент, ему одних
                  // inset-ов мало, без width/height он берёт свои дефолтные
                  // 300×150 и диагональ уезжает через пол-экрана.
                  className="pointer-events-none absolute -left-[2px] -top-[2px] h-[calc(100%+4px)] w-[calc(100%+4px)] text-fill-danger opacity-80"
                >
                  <line
                    x1="0"
                    y1="100"
                    x2="100"
                    y2="0"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    )}
  </div>
);
